<template>
  <div
    class="modern-container"
    aria-label="Liste des demandes de modification de prix"
    role="main"
  >
    <div class="tickets-header">
      <div class="header-content">
        <h1 class="simple-title">
          Demandes de modification de prix
        </h1>
      </div>
    </div>
    <div class="modern-card">
      <div class="card-header">
        <div
          class="stats-row"
          aria-label="Statistiques des demandes de modification de prix"
        >
          <div class="stat-item">
            <div class="stat-icon-wrapper text-primary">
              <feather-icon icon="FileTextIcon" size="24" />
            </div>
            <span
              class="stat-number"
              aria-live="polite"
            >{{ pagination.count || requests.length }}</span>
            <span class="stat-label">Total</span>
          </div>
          <div class="stat-item">
            <div class="stat-icon-wrapper text-warning">
              <feather-icon icon="ClockIcon" size="24" />
            </div>
            <span
              class="stat-number"
              aria-live="polite"
            >{{ getPendingCount() }}</span>
            <span class="stat-label">En attente</span>
          </div>
          <div class="stat-item">
            <div class="stat-icon-wrapper text-success">
              <feather-icon icon="CheckCircleIcon" size="24" />
            </div>
            <span
              class="stat-number"
              aria-live="polite"
            >{{ getAcceptedCount() }}</span>
            <span class="stat-label">Acceptées</span>
          </div>
          <div class="stat-item">
            <div class="stat-icon-wrapper text-danger">
              <feather-icon icon="XCircleIcon" size="24" />
            </div>
            <span
              class="stat-number"
              aria-live="polite"
            >{{ getRejectedCount() }}</span>
            <span class="stat-label">Rejetées</span>
          </div>
        </div>
        <div
          class="filter-bar mt-4 mb-2 px-0 px-md-3 py-2 d-flex flex-wrap align-items-center justify-content-between"
          style="background: #f8f9fa; border-radius: 12px; box-shadow: 0 1px 4px rgba(0,0,0,0.04);"
        >
          <div class="d-flex flex-wrap align-items-center gap-3">
            <div class="filter-group d-flex align-items-center mr-3 mb-2 mb-md-0">
              <i class="fas fa-search text-muted mr-2" />
              <input
                v-model="search"
                type="text"
                class="form-control filter-input"
                placeholder="Rechercher commande, fournisseur..."
                aria-label="Recherche de modification de prix"
                style="min-width: 220px; border-radius: 8px; border: 1px solid #e2e8f0; box-shadow: none;"
                @keyup.enter="applyFilters"
              >
            </div>
            <div class="filter-group d-flex align-items-center mr-3 mb-2 mb-md-0">
              <i class="fas fa-filter text-muted mr-2" />
              <select
                v-model="statusFilter"
                class="form-control filter-select"
                aria-label="Filtrer par statut"
                style="min-width: 170px; border-radius: 8px; border: 1px solid #e2e8f0; box-shadow: none;"
                @change="applyFilters"
              >
                <option value="">
                  Tous les statuts
                </option>
                <option value="pending">
                  En attente
                </option>
                <option value="accepted">
                  Acceptée
                </option>
                <option value="rejected">
                  Rejetée
                </option>
              </select>
            </div>
            <div class="filter-group d-flex align-items-center mr-3 mb-2 mb-md-0">
              <i class="fas fa-calendar text-muted mr-2" />
              <select
                v-model="dateRangeFilter"
                class="form-control filter-select"
                aria-label="Filtrer par période"
                style="min-width: 170px; border-radius: 8px; border: 1px solid #e2e8f0; box-shadow: none;"
                @change="applyFilters"
              >
                <option value="">
                  Toutes les dates
                </option>
                <option value="today">
                  Aujourd'hui
                </option>
                <option value="yesterday">
                  Hier
                </option>
                <option value="this_week">
                  Cette semaine
                </option>
                <option value="last_week">
                  Semaine dernière
                </option>
                <option value="this_month">
                  Ce mois
                </option>
                <option value="last_month">
                  Mois dernier
                </option>
                <option value="last_7_days">
                  7 derniers jours
                </option>
                <option value="last_30_days">
                  30 derniers jours
                </option>
              </select>
            </div>
            <button
              class="btn btn-outline-secondary ml-2"
              aria-label="Réinitialiser les filtres"
              style="border-radius: 8px;"
              @click="resetFilters"
            >
              <i class="fas fa-times mr-1" /> Réinitialiser
            </button>
          </div>
        </div>
        <div
          v-if="search || statusFilter || dateRangeFilter"
          class="applied-filters mt-2 d-flex flex-wrap align-items-center gap-2"
        >
          <span
            v-if="search"
            class="applied-filter-chip"
          >
            <i class="fas fa-search mr-1" />
            <span>{{ search }}</span>
            <button
              class="clear-chip-btn ml-1"
              aria-label="Effacer la recherche"
              @click="search = ''; applyFilters()"
            >&times;</button>
          </span>
          <span
            v-if="statusFilter"
            class="applied-filter-chip"
          >
            <i class="fas fa-filter mr-1" />
            <span>{{ getStatusLabel(statusFilter) }}</span>
            <button
              class="clear-chip-btn ml-1"
              aria-label="Effacer le filtre de statut"
              @click="statusFilter = ''; applyFilters()"
            >&times;</button>
          </span>
          <span
            v-if="dateRangeFilter"
            class="applied-filter-chip"
          >
            <i class="fas fa-calendar mr-1" />
            <span>{{ getDateRangeLabel(dateRangeFilter) }}</span>
            <button
              class="clear-chip-btn ml-1"
              aria-label="Effacer le filtre de date"
              @click="dateRangeFilter = ''; applyFilters()"
            >&times;</button>
          </span>
          <span
            v-if="search || statusFilter || dateRangeFilter"
            class="ml-2"
          >
            <button
              class="btn btn-link p-0 applied-filters-clear-all"
              aria-label="Effacer tous les filtres"
              @click="resetFilters"
            >
              <i class="fas fa-times-circle mr-1" /> Effacer tous les filtres
            </button>
          </span>
        </div>
        <!-- Debug info -->
        <div
          v-if="pagination.count !== undefined"
          class="debug-info mt-2 text-muted small"
        >
          <i class="fas fa-info-circle mr-1" />
          Pagination: {{ pagination.current_page || currentPage }}/{{ pagination.total_pages }} pages,
          {{ pagination.page_size }} par page,
          {{ pagination.count }} total
        </div>
      </div>
      <div class="modern-table-container">
        <template v-if="loading">
          <div class="loading-state">
            <b-spinner
              variant="danger"
              label="Chargement des demandes..."
              style="width: 3rem; height: 3rem;"
              class="mb-3"
            />
            <span>Chargement des demandes...</span>
          </div>
        </template>
        <template v-else-if="requests.length === 0">
          <div class="text-center py-5">
            <div
              class="no-results-card shadow-sm enhanced-empty-state"
              style="background: #fff; border-radius: 20px; padding: 2.5rem 2rem; max-width: 520px; margin: 0 auto; box-shadow: 0 8px 32px rgba(156,163,175,0.10);"
            >
              <div class="mb-4">
                <img
                  src="/public/img/icons/empty-support.svg"
                  alt="Aucune demande"
                  style="height: 110px; opacity: 0.85;"
                  onerror="this.style.display='none'"
                >
                <i
                  v-if="false"
                  class="fas fa-inbox fa-4x text-muted"
                />
              </div>
              <h3
                class="text-muted mb-2 font-weight-bold"
                style="font-size: 1.5rem;"
              >
                Aucune demande trouvée
              </h3>
              <p
                class="text-muted mb-4"
                style="font-size: 1.08rem;"
              >
                <span v-if="search || statusFilter || dateRangeFilter">
                  <span style="font-weight: 500;">Aucune demande ne correspond à vos critères.</span><br>
                  Essayez d'élargir votre recherche ou de réinitialiser les filtres pour voir plus de résultats.
                </span>
                <span v-else>
                  <span style="font-weight: 500;">Il n'y a actuellement aucune demande de modification de prix.</span><br>
                  Les demandes apparaîtront ici lorsqu'elles seront créées.
                </span>
              </p>
              <div v-if="search || statusFilter || dateRangeFilter">
                <b-button
                  variant="outline-primary"
                  size="lg"
                  class="mt-1 px-4"
                  @click="resetFilters"
                >
                  <i class="fas fa-sync-alt mr-1" /> Réinitialiser les filtres
                </b-button>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <b-table
            :items="requests"
            :fields="tableFields"
            responsive="sm"
            class="modern-table pricechange-table"
            hover
            borderless
            aria-label="Tableau des demandes de modification de prix"
            caption-top
            @row-clicked="openQuickView"
          >
            <template #cell(order)="{ item }">
              <div class="order-cell">
                <span
                  v-b-tooltip.hover
                  :title="'Référence: #' + (item.order && item.order.id ? item.order.id : item.order)"
                  class="order-badge"
                  style="cursor:pointer"
                  @click.stop="copyOrderId(item.order && item.order.id)"
                >
                  #{{ (item.order && item.order.id) ? item.order.id : item.order }}
                </span>
                <span
                  v-if="item.order && item.order.name"
                  class="order-info ml-2"
                >{{ item.order.name }}</span>
                <span
                  v-else
                  class="order-info ml-2"
                >-</span>
              </div>
            </template>
            <template #cell(supplier)="{ item }">
              <div class="user-cell">
                <span class="user-name">{{ (item.supplier && (item.supplier.full_name || item.supplier.username)) ? (item.supplier.full_name || item.supplier.username) : '-' }}</span>
              </div>
            </template>
            <template #cell(old_price)="{ item }">
              <span>{{ item.old_price }} DT</span>
            </template>
            <template #cell(new_price)="{ item }">
              <span>{{ item.new_price }} DT</span>
            </template>
            <template #cell(status)="{ item }">
              <span :class="['status-pill', getStatusClass(item.status)]">
                <i :class="getStatusIcon(item.status)" />
                {{ getStatusLabel(item.status) }}
              </span>
            </template>
            <template #cell(created_at)="{ item }">
              <div class="date-cell">
                <span class="date-text">{{ formatDate(item.created_at) }}</span>
              </div>
            </template>
            <template #cell(actions)="{ item }">
              <div class="actions-cell">
                <b-button
                  v-if="item.status && item.status.toLowerCase() === 'pending' && canManageRequests"
                  variant="success"
                  size="sm"
                  class="mr-2"
                  @click.stop="showAcceptModal(item)"
                >
                  <i class="fas fa-check mr-1" /> Accepter
                </b-button>
                <b-button
                  v-if="item.status && item.status.toLowerCase() === 'pending' && canManageRequests"
                  variant="danger"
                  size="sm"
                  @click.stop="showRejectModal(item)"
                >
                  <i class="fas fa-times mr-1" /> Rejeter
                </b-button>
                <span v-if="!item.status || item.status.toLowerCase() !== 'pending' || !canManageRequests">-</span>
              </div>
            </template>
          </b-table>

          <!-- Enhanced Pagination Controls -->
          <div
            v-if="pagination.count > pagination.page_size"
            class="pagination-container d-flex justify-content-between align-items-center mt-4 px-3"
          >
            <div class="pagination-info">
              <span class="text-muted">
                Affichage de {{ getDisplayRange() }} sur {{ pagination.count }} demandes
              </span>
              <div class="page-size-selector ml-3 d-inline-block">
                <label class="text-muted mr-2">Items par page:</label>
                <select
                  v-model="pageSize"
                  class="form-control d-inline-block"
                  style="width: auto; font-size: 0.875rem;"
                  @change="changePageSize"
                >
                  <option value="10">
                    10
                  </option>
                  <option value="20">
                    20
                  </option>
                  <option value="50">
                    50
                  </option>
                  <option value="100">
                    100
                  </option>
                </select>
              </div>
            </div>
            <b-pagination
              v-model="currentPage"
              :total-rows="pagination.count"
              :per-page="pagination.page_size"
              :limit="5"
              size="md"
              class="mb-0"
              @change="onPageChange"
            />
          </div>
        </template>
      </div>
    </div>
    <price-change-request-quick-view
      :visible="showQuickView"
      :request="quickViewRequest"
      @close="closeQuickView"
    />
    <b-modal
      id="confirm-modal"
      v-model="showModal"
      :title="modalTitle"
      ok-title="Confirmer"
      cancel-title="Annuler"
      centered
      @ok="confirmAction"
    >
      {{ modalMessage }}
      <b-form-textarea
        v-if="modalType === 'reject'"
        v-model="adminResponse"
        placeholder="Motif du refus (optionnel)"
        rows="2"
        class="mt-2"
      />
    </b-modal>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import {
  BButton, BTable, BModal, BFormTextarea, BPagination, BSpinner,
} from 'bootstrap-vue'
import storeAuth from '@/store/store'
import PriceChangeRequestQuickView from './PriceChangeRequestQuickView.vue'

export default {
  name: 'PriceChangeRequestList',
  components: {
    BButton, BTable, BModal, BFormTextarea, BPagination, BSpinner, PriceChangeRequestQuickView,
  },
  data() {
    return {
      requests: [],
      loading: false,
      fields: [
        { key: 'order', label: 'Commande', sortable: false },
        { key: 'supplier', label: 'Fournisseur', sortable: false },
        { key: 'old_price', label: 'Ancien prix', sortable: true },
        { key: 'new_price', label: 'Nouveau prix', sortable: true },
        { key: 'reason', label: 'Motif', sortable: false },
        { key: 'status', label: 'Statut', sortable: true },
        { key: 'created_at', label: 'Demandée le', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      search: '',
      statusFilter: '',
      dateRangeFilter: '',
      showModal: false,
      modalType: '',
      modalTitle: '',
      modalMessage: '',
      modalRequest: null,
      adminResponse: '',
      showQuickView: false,
      quickViewRequest: {},
      currentPage: 1,
      pageSize: 20,
      pagination: {
        count: 0,
        next: null,
        previous: null,
        page_size: 20,
        current_page: 1,
        total_pages: 1,
      },
    }
  },
  computed: {
    dateRangeFilterLabel() {
      switch (this.dateRangeFilter) {
        case 'today': return "Aujourd'hui"
        case 'yesterday': return 'Hier'
        case 'this_week': return 'Cette semaine'
        case 'last_week': return 'Semaine dernière'
        case 'this_month': return 'Ce mois'
        case 'last_month': return 'Mois dernier'
        case 'last_7_days': return '7 derniers jours'
        case 'last_30_days': return '30 derniers jours'
        default: return this.dateRangeFilter
      }
    },
    canManageRequests() {
      const userRole = storeAuth.getters.userRole || storeAuth.state.user?.role
      return userRole === 'admin'
    },
    tableFields() {
      // derive fields to avoid referencing computeds inside data()
      return this.fields.map(f => {
        if (f.key !== 'actions') return f
        return {
          ...f,
          thClass: this.canManageRequests ? '' : 'd-none',
          tdClass: this.canManageRequests ? '' : 'd-none',
        }
      })
    },
  },
  created() {
    this.applyFilters()
  },
  methods: {
    openQuickView(request) {
      this.quickViewRequest = request
      this.showQuickView = true
    },
    closeQuickView() {
      this.showQuickView = false
      this.quickViewRequest = {}
    },
    async fetchRequests(params = {}) {
      this.loading = true
      try {
        const queryParams = {
          page: this.currentPage,
          page_size: this.pageSize,
          ...params,
        }
        if (this.search) queryParams.search = this.search
        if (this.statusFilter) queryParams.status = this.statusFilter
        if (this.dateRangeFilter) queryParams.date_range = this.dateRangeFilter

        const { data } = await axios.get('/api/support/pricechangerequests/', { params: queryParams })
        this.requests = data.results || data
        this.pagination = {
          count: data.count || 0,
          next: data.next,
          previous: data.previous,
          page_size: data.page_size || this.pageSize,
          current_page: data.current_page || this.currentPage,
          total_pages: data.total_pages || 1,
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching requests:', error)
        this.$bvToast.toast('Erreur lors du chargement des demandes', { variant: 'danger' })
      } finally {
        this.loading = false
      }
    },
    onPageChange(page) {
      this.currentPage = page
      this.applyFilters(false)
    },
    changePageSize() {
      this.currentPage = 1
      this.applyFilters(false)
    },
    getDisplayRange() {
      const start = (this.currentPage - 1) * this.pagination.page_size + 1
      const end = Math.min(this.currentPage * this.pagination.page_size, this.pagination.count)
      return `${start}-${end}`
    },
    applyFilters(resetPage = true) {
      if (resetPage) this.currentPage = 1
      const params = {}
      if (this.statusFilter) params.status = this.statusFilter
      if (this.search) params.search = this.search
      if (this.dateRangeFilter) params.date_range = this.dateRangeFilter
      this.fetchRequests(params)
    },
    resetFilters() {
      this.statusFilter = ''
      this.search = ''
      this.dateRangeFilter = ''
      this.currentPage = 1
      this.applyFilters(false)
    },
    getDateRangeLabel(range) {
      switch (range) {
        case 'today': return "Aujourd'hui"
        case 'yesterday': return 'Hier'
        case 'this_week': return 'Cette semaine'
        case 'last_week': return 'Semaine dernière'
        case 'this_month': return 'Ce mois'
        case 'last_month': return 'Mois dernier'
        case 'last_7_days': return '7 derniers jours'
        case 'last_30_days': return '30 derniers jours'
        default: return 'Toutes les dates'
      }
    },
    showAcceptModal(request) {
      this.modalType = 'accept'
      this.modalTitle = 'Accepter la demande de modification de prix'
      this.modalMessage = `Confirmer l'acceptation de la demande pour la commande #${request.order && request.order.id ? request.order.id : request.order} ?`
      this.modalRequest = request
      this.adminResponse = ''
      this.showModal = true
    },
    showRejectModal(request) {
      this.modalType = 'reject'
      this.modalTitle = 'Rejeter la demande de modification de prix'
      this.modalMessage = `Confirmer le rejet de la demande pour la commande #${request.order && request.order.id ? request.order.id : request.order} ?`
      this.modalRequest = request
      this.adminResponse = ''
      this.showModal = true
    },
    async confirmAction() {
      if (!this.modalRequest) return
      const { id } = this.modalRequest
      try {
        if (this.modalType === 'accept') {
          await axios.patch(`/api/support/pricechangerequests/${id}/process/`, { action: 'accept', admin_response: this.adminResponse })
          this.$bvToast.toast('Demande acceptée avec succès.', { variant: 'success', autoHideDelay: 2500 })
        } else if (this.modalType === 'reject') {
          await axios.patch(`/api/support/pricechangerequests/${id}/process/`, { action: 'reject', admin_response: this.adminResponse })
          this.$bvToast.toast('Demande rejetée.', { variant: 'danger', autoHideDelay: 2500 })
        }
        this.applyFilters() // Use applyFilters instead of fetchRequests to maintain filters
      } catch (e) {
        this.$bvToast.toast('Erreur lors du traitement.', { variant: 'danger', autoHideDelay: 2500 })
      }
      this.showModal = false
    },
    copyOrderId(orderId) {
      if (!orderId) return
      navigator.clipboard.writeText(orderId.toString()).then(() => {
        this.$bvToast.toast(`Référence ${orderId} copiée!`, { variant: 'success', autoHideDelay: 2000 })
      })
    },
    getPendingCount() {
      return this.requests.filter(r => r.status && r.status.toLowerCase() === 'pending').length
    },
    getAcceptedCount() {
      return this.requests.filter(r => r.status && r.status.toLowerCase() === 'accepted').length
    },
    getRejectedCount() {
      return this.requests.filter(r => r.status && r.status.toLowerCase() === 'rejected').length
    },
    getStatusClass(status) {
      if (!status) return 'status-open'
      if (status.toLowerCase() === 'pending') return 'status-open'
      if (status.toLowerCase() === 'accepted') return 'status-resolved'
      if (status.toLowerCase() === 'rejected') return 'status-closed'
      return 'status-open'
    },
    getStatusIcon(status) {
      if (!status) return 'fas fa-clock'
      if (status.toLowerCase() === 'pending') return 'fas fa-clock'
      if (status.toLowerCase() === 'accepted') return 'fas fa-check'
      if (status.toLowerCase() === 'rejected') return 'fas fa-times'
      return 'fas fa-clock'
    },
    getStatusLabel(status) {
      if (!status) return '-'
      const s = status.toLowerCase()
      if (s === 'pending') return 'En attente'
      if (s === 'accepted') return 'Acceptée'
      if (s === 'rejected') return 'Rejetée'
      return status
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.modern-container {
  padding: 1.5rem;
  animation: slideUp 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.tickets-header {
  background: linear-gradient(135deg, #090909 0%, #0d4399 100%);
  padding: 2.5rem 2rem;
  border-radius: 16px;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(13, 67, 153, 0.2);
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: url('data:image/svg+xml;utf8,<svg width="20" height="20" xmlns="http://www.w3.org/2000/svg"><circle cx="2" cy="2" r="2" fill="rgba(255,255,255,0.05)"/></svg>');
    opacity: 0.5;
  }
}

.simple-title {
  font-weight: 700;
  margin: 0;
  color: white;
  position: relative;
  z-index: 1;
  font-size: 1.8rem;
  letter-spacing: 0.5px;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.stats-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 2rem;

  .stat-item {
    flex: 1;
    min-width: 200px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    padding: 1.5rem;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    align-items: center;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    border: 1px solid rgba(0, 0, 0, 0.03);
    position: relative;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 4px;
      background: linear-gradient(90deg, #0d4399, #4a90e2);
      transform: scaleX(0);
      transition: transform 0.3s ease;
      transform-origin: left;
    }

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 12px 40px rgba(13, 67, 153, 0.12);

      &::after {
        transform: scaleX(1);
      }
    }

    .stat-icon-wrapper {
      margin-bottom: 0.75rem;
      padding: 0.75rem;
      border-radius: 50%;
      background: rgba(0,0,0,0.03);
    }

    .stat-number {
      font-size: 2.2rem;
      font-weight: 800;
      color: #090909;
      margin-bottom: 0.2rem;
      line-height: 1;
    }
    .stat-label {
      color: #6e6b7b;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1px;
      font-size: 0.85rem;
    }
  }
}

.modern-card {
  border-radius: 16px;
  border: none;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
  background: white;
  overflow: hidden;
}

.filter-bar {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid rgba(0,0,0,0.03);
}

.filter-select, .filter-input {
  border: 1px solid #e0e0e0 !important;
  border-radius: 8px !important;
  background-color: white;
  padding: 0.5rem 1rem;
  transition: all 0.2s;
  &:focus {
    border-color: #0d4399 !important;
    box-shadow: 0 0 0 0.2rem rgba(13, 67, 153, 0.25) !important;
    outline: none;
  }
}

::v-deep .table-hover tbody tr {
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(13, 67, 153, 0.03) !important;
    transform: scale(1.005);
    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
    border-radius: 8px;
  }
}

/* Pagination Styles */
.pagination-container {
  border-top: 1px solid #f1f5f9;
  padding-top: 1rem;
  background: #f8f9fa;
  border-radius: 0 0 20px 20px;
}

.pagination-info {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

::v-deep .pagination .page-link {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-weight: 500;
  margin: 0 2px;
}

::v-deep .pagination .page-item.active .page-link {
  background: linear-gradient(135deg, #0d4399 0%, #4a90e2 100%);
  border-color: #0d4399;
  color: white;
}

::v-deep .pagination .page-link:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
  color: #1e293b;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}

.status-pill {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}
.status-open {
  background: linear-gradient(135deg, #090909 0%, #1a202c 100%);
  color: white;
}
.status-resolved {
  background: #2FDD6F;
  color: white;
}
.status-closed {
  background: linear-gradient(135deg, #0d4399 0%, #0d4399 100%);
  color: white;
}

.order-badge {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #000000;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.order-badge:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(13, 67, 153, 0.15);
  background: linear-gradient(135deg, #0d4399 0%, #4a90e2 100%);
  color: white;
}
</style>
